import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import { control, table } from "@strust/ui/recipes";
import { slots } from "../slots.ts";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, ColumnsIcon } from "../icons/icons.tsx";
import { Button } from "../primitives/button.tsx";
import { Checkbox } from "../primitives/choices.tsx";
import { EmptyState, ErrorState, Skeleton } from "../primitives/feedback.tsx";
import { Pagination } from "./navigation.tsx";

export type SortDirection = "asc" | "desc";

export interface SortState {
  readonly columnId: string;
  readonly direction: SortDirection;
}

export interface DataTableColumn<Row> {
  readonly id: string;
  /** Localized header text. */
  readonly header: string;
  readonly cell: (row: Row) => ReactNode;
  readonly sortable?: boolean;
  /** Columns are hideable unless this is false (e.g. the identifier column). */
  readonly hideable?: boolean;
  /** Right-aligned numeric column (tabular figures). */
  readonly numeric?: boolean;
  /**
   * Free-text column (comments, details): its cells wrap, clamped to two lines. Every other cell
   * stays on one line (design D1).
   */
  readonly wrap?: boolean;
  /**
   * The column takes the width the others leave (a comment, details, an email): its text stays on
   * one line and is cut with an ellipsis instead of widening the table; give the cell a `title`
   * with the whole text. At most one column of a table.
   */
  readonly grow?: boolean;
  /**
   * The header text is visually hidden and only names the column for assistive technology (a
   * selection checkbox column), so the column is as narrow as its cells.
   */
  readonly hideHeader?: boolean;
}

export interface DataTableLabels {
  readonly columnChooser: string;
  readonly previousPage: string;
  readonly nextPage: string;
  /** Already formatted, e.g. "Page 2 of 5". */
  readonly pageStatus: string;
  readonly loading: string;
  readonly emptyTitle: string;
  readonly emptyDescription?: string;
  readonly errorTitle: string;
  readonly retry: string;
}

export interface DataTableProps<Row> {
  /** Accessible name and visible title of the table. */
  readonly caption: string;
  /** Short context next to the title, e.g. "48,920 in period". */
  readonly meta?: ReactNode;
  /** Extra toolbar actions (export, filters), end side. */
  readonly actions?: ReactNode;
  readonly columns: readonly DataTableColumn<Row>[];
  readonly rows: readonly Row[];
  readonly getRowId: (row: Row) => string;
  readonly labels: DataTableLabels;
  readonly status?: "ready" | "loading" | "error";
  readonly onRetry?: () => void;
  readonly sort?: SortState | null;
  readonly onSortChange?: (sort: SortState | null) => void;
  /** 1-based page, server-side pagination. */
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPageChange: (page: number) => void;
  /** Controlled visible columns; omit to show all. */
  readonly visibleColumnIds?: readonly string[];
  readonly onVisibleColumnsChange?: (ids: readonly string[]) => void;
  /** Highlights a row (e.g. the transaction selected on the map). */
  readonly isRowSelected?: (row: Row) => boolean;
  /** Marks rows with a secondary line (`data-two-line`): they may be up to 64 px high. */
  readonly isRowTwoLine?: (row: Row) => boolean;
  /** `compact` (default): 44 px rows and a 36 px header; `comfortable`: the roomier padding. */
  readonly density?: "comfortable" | "compact";
  /**
   * The caption stays the table's accessible name but is not shown (a screen whose card already
   * has a heading or tabs). The toolbar still shows when it has meta, actions or columns.
   */
  readonly captionHidden?: boolean;
  /**
   * Content of the card header instead of the visible title (a `TabList`): the caption stays the
   * table's accessible name, visually hidden. Pair it with `panel` so the tabs control the table.
   */
  readonly header?: ReactNode;
  /** Makes everything under the header a `tabpanel` (its `id`, labelled by the selected tab). */
  readonly panel?: { readonly id: string; readonly labelledBy: string };
  /** A "Rows per page" control at the start of the footer. */
  readonly rowsPerPage?: RowsPerPage;
  /** Accessible name of a numbered page button; shows the page numbers in the footer. */
  readonly pageLabel?: (page: number) => string;
  readonly className?: string;
}

export interface RowsPerPage {
  /** Localized label (`Rows per page`). */
  readonly label: string;
  readonly value: number;
  readonly options: readonly number[];
  readonly onChange: (size: number) => void;
}

/** Next sort in the cycle: ascending, descending, unsorted. */
export function nextSort(
  current: SortState | null | undefined,
  columnId: string,
): SortState | null {
  if (current?.columnId !== columnId) return { columnId, direction: "asc" };
  return current.direction === "asc" ? { columnId, direction: "desc" } : null;
}

const PANEL = css({ display: "flex", flexDirection: "column", minWidth: "0" });
const ROWS_PER_PAGE = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "2",
  fontSize: "sm",
  color: "text.2",
});
const ROWS_SELECT = css({ width: "18", fontFamily: "mono", fontSize: "sm" });
const NUMERIC = css({ textAlign: "end", fontVariantNumeric: "tabular-nums" });
const WRAP = css({ whiteSpace: "normal", overflowWrap: "anywhere", minWidth: "40" });
const GROW_HEAD = css({ width: "full" });
const GROW_CELL = css({ width: "full", maxWidth: "0", minWidth: "24" });
const GROW_TEXT = css({ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" });
const CLAMP = css({ lineClamp: 2, overflow: "hidden", overflowWrap: "anywhere" });

/** Data table: toolbar (title, meta, columns), sortable mono headers, states and pagination. */
export function DataTable<Row>(props: DataTableProps<Row>) {
  const {
    caption,
    meta,
    actions,
    columns,
    rows,
    getRowId,
    labels,
    status = "ready",
    onRetry,
    sort,
    onSortChange,
    page,
    pageSize,
    total,
    onPageChange,
    visibleColumnIds,
    onVisibleColumnsChange,
    isRowSelected,
    isRowTwoLine,
    density = "compact",
    captionHidden = false,
    header,
    panel,
    rowsPerPage,
    pageLabel,
    className,
  } = props;
  const visible = columns.filter((c) => !visibleColumnIds || visibleColumnIds.includes(c.id));
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const captionId = useId();
  const s = slots(table({ density, tabbed: header !== undefined }));

  const body = (
    <>
      {status === "error" ? (
        <ErrorState
          bare
          title={labels.errorTitle}
          retryLabel={labels.retry}
          onRetry={onRetry ?? noop}
        />
      ) : (
        <TableScroll labelledBy={captionId}>
          <table className={s.table} aria-labelledby={captionId}>
            <thead className={s.head}>
              <tr>
                {visible.map((column) => {
                  const active = sort?.columnId === column.id ? sort.direction : undefined;
                  return (
                    <th
                      key={column.id}
                      scope="col"
                      className={cx(
                        s.headerCell,
                        column.numeric ? NUMERIC : undefined,
                        column.grow ? GROW_HEAD : undefined,
                      )}
                      aria-sort={
                        column.sortable
                          ? active === "asc"
                            ? "ascending"
                            : active === "desc"
                              ? "descending"
                              : "none"
                          : undefined
                      }
                    >
                      {column.sortable && onSortChange ? (
                        <button
                          type="button"
                          className={s.sort}
                          onClick={() => {
                            onSortChange(nextSort(sort, column.id));
                          }}
                        >
                          {column.header}
                          {active === "asc" ? (
                            <ArrowUpIcon aria-hidden="true" />
                          ) : active === "desc" ? (
                            <ArrowDownIcon aria-hidden="true" />
                          ) : (
                            <ArrowUpDownIcon aria-hidden="true" />
                          )}
                        </button>
                      ) : column.hideHeader ? (
                        <span className={css({ srOnly: true })}>{column.header}</span>
                      ) : (
                        column.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {status === "loading" ? (
                <LoadingRows columns={visible.length} label={labels.loading} cellClass={s.cell} />
              ) : (
                rows.map((row) => (
                  <tr
                    key={getRowId(row)}
                    className={s.row}
                    data-selected={isRowSelected?.(row) ? "" : undefined}
                    data-two-line={isRowTwoLine?.(row) ? "" : undefined}
                  >
                    {visible.map((column) => (
                      <td
                        key={column.id}
                        className={cx(
                          s.cell,
                          column.numeric ? NUMERIC : undefined,
                          column.wrap ? WRAP : undefined,
                          column.grow ? GROW_CELL : undefined,
                        )}
                        data-wrap={column.wrap ? "" : undefined}
                      >
                        {column.wrap ? (
                          <div className={CLAMP}>{column.cell(row)}</div>
                        ) : column.grow ? (
                          <div className={GROW_TEXT}>{column.cell(row)}</div>
                        ) : (
                          column.cell(row)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {status === "ready" && rows.length === 0 ? (
            <EmptyState
              bare
              title={labels.emptyTitle}
              {...(labels.emptyDescription === undefined
                ? {}
                : { description: labels.emptyDescription })}
            />
          ) : null}
        </TableScroll>
      )}
      {status === "ready" && total > 0 ? (
        <div className={s.footer}>
          {rowsPerPage ? <RowsPerPageControl {...rowsPerPage} /> : null}
          <Pagination
            label={caption}
            page={page}
            pages={pages}
            onPageChange={onPageChange}
            previousLabel={labels.previousPage}
            nextLabel={labels.nextPage}
            status={labels.pageStatus}
            {...(pageLabel ? { pageLabel } : {})}
          />
        </div>
      ) : null}
    </>
  );

  return (
    <div className={cx(s.root, className)} aria-busy={status === "loading" || undefined}>
      {captionHidden && !header && !meta && !actions && !onVisibleColumnsChange ? (
        <p id={captionId} className={css({ srOnly: true })}>
          {caption}
        </p>
      ) : (
        <div className={s.toolbar}>
          <p id={captionId} className={captionHidden ? css({ srOnly: true }) : s.title}>
            {caption}
          </p>
          {header}
          {meta ? <span className={s.meta}>{meta}</span> : null}
          {actions || onVisibleColumnsChange ? (
            <div className={s.tools}>
              {actions}
              {onVisibleColumnsChange ? (
                <ColumnChooser
                  columns={columns}
                  visibleIds={visible.map((c) => c.id)}
                  label={labels.columnChooser}
                  onChange={onVisibleColumnsChange}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {panel ? (
        <div role="tabpanel" id={panel.id} aria-labelledby={panel.labelledBy} className={PANEL}>
          {body}
        </div>
      ) : (
        body
      )}
    </div>
  );
}

function RowsPerPageControl({ label, value, options, onChange }: RowsPerPage) {
  return (
    <label className={ROWS_PER_PAGE}>
      <span>{label}</span>
      <select
        className={cx(control({ kind: "select", size: "sm" }), ROWS_SELECT)}
        value={String(value)}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
      >
        {options.map((option) => (
          <option key={option} value={String(option)}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function noop(): void {
  /* no retry handler supplied */
}

function LoadingRows({
  columns,
  label,
  cellClass,
}: {
  readonly columns: number;
  readonly label: string;
  readonly cellClass: string;
}) {
  return (
    <>
      {[0, 1, 2, 3].map((row) => (
        <tr key={row}>
          <td colSpan={columns} className={cellClass}>
            <Skeleton width={row % 2 === 0 ? "100%" : "70%"} />
            {row === 0 ? <span className={css({ srOnly: true })}>{label}</span> : null}
          </td>
        </tr>
      ))}
    </>
  );
}

interface ColumnChooserProps<Row> {
  readonly columns: readonly DataTableColumn<Row>[];
  readonly visibleIds: readonly string[];
  readonly label: string;
  readonly onChange: (ids: readonly string[]) => void;
}

function ColumnChooser<Row>({ columns, visibleIds, label, onChange }: ColumnChooserProps<Row>) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  const s = slots(table());

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const outside = (event: MouseEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("mousedown", outside);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("mousedown", outside);
    };
  }, [open]);

  const toggle = (id: string, checked: boolean) => {
    const next = columns
      .filter((c) => (c.id === id ? checked : visibleIds.includes(c.id)))
      .map((c) => c.id);
    if (next.length > 0) onChange(next);
  };

  return (
    <div className={s.chooser} ref={root}>
      <Button
        size="sm"
        icon={<ColumnsIcon />}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {label}
      </Button>
      {open ? (
        <div id={panelId} className={s.chooserPanel} role="group" aria-label={label}>
          {columns.map((column) => {
            const checked = visibleIds.includes(column.id);
            return (
              <Checkbox
                key={column.id}
                label={column.header}
                checked={checked}
                disabled={column.hideable === false || (checked && visibleIds.length === 1)}
                onChange={(event) => {
                  toggle(column.id, event.target.checked);
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export interface TableScrollProps {
  /** Id of the element naming the table, the name of the scroll region. */
  readonly labelledBy: string;
  readonly children: ReactNode;
}

/**
 * The horizontal scroll container of a table. When the table is wider than the space it has (many
 * columns at 1280 px), keyboard users must be able to scroll it (WCAG 2.1.1, axe
 * `scrollable-region-focusable`): the container then becomes a focusable region named by the
 * table's title. A table that fits adds no extra tab stop.
 */
export function TableScroll({ labelledBy, children }: TableScrollProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      setScrollable(element.scrollWidth > element.clientWidth + 1);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    const tableElement = element.firstElementChild;
    if (tableElement) observer.observe(tableElement);
    return () => {
      observer.disconnect();
    };
  });

  return (
    <div
      ref={ref}
      className={table().scroll}
      {...(scrollable ? { tabIndex: 0, role: "region", "aria-labelledby": labelledBy } : {})}
    >
      {children}
    </div>
  );
}
